"use client";
import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import MenuItem from "./MenuItem";
import ProfileMenu from "./ProfileMenu";
import IMAGES from "@/data/assetData";
import { usePathname } from "next/navigation";
import SearchPill from "@/components/SearchForm/SearchPill";
import { useDispatch, useSelector } from "react-redux";
import useCartCount from "@/hooks/useCartCount";
import Link from "next/link";
import { setCityname } from "@/redux/search/searchSlice";
import Image from "next/image";
import { BASE_URL_CITY } from "@/data/assetData";


const PAGE_URLS_WITHOUT_SEARCH = ["/search/", "/cart/"];

const NavBar = () => {
  const pathname = usePathname();
  const dispatch = useDispatch();
  const { totalCartCount, isCartLoaded } = useSelector((state) => state.order);
  const refetchCart = useCartCount();
  const navRef = useRef(null);
  const [showmenu, setShowmenu] = useState(false);
  const [showHeaderSearch, setShowHeaderSearch] = useState(false);
  const [isScrolled, setIsScrolled] = useState(true);
  const [prevPathname, setPrevPathname] = useState(pathname);

  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    if (PAGE_URLS_WITHOUT_SEARCH.includes(pathname)) {
      setShowHeaderSearch(false);
    }
  }

  useLayoutEffect(() => {
    const navbar = navRef.current;
    if (!navbar) return;

    let rafId;

    const update = () => {
      const darkSections = document.querySelectorAll('[data-header-theme="dark"]');

      if (darkSections.length === 0) {
        setIsScrolled(true);
        return;
      }

      const navBottom = navbar.getBoundingClientRect().bottom;
      let overDark = false;

      darkSections.forEach((section) => {
        const rect = section.getBoundingClientRect();
        if (rect.top < navBottom && rect.bottom > 0) {
          overDark = true;
        }
      });

      setIsScrolled(!overDark);
    };

    const onScroll = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(update);
    };

    update();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [pathname]);

  useEffect(() => {
    if (PAGE_URLS_WITHOUT_SEARCH.includes(pathname)) {
      return;
    }

    let observer = null;

    const timeout = setTimeout(() => {
      const heroSearchPill = document.querySelector("[data-search-pill]");
      if (!heroSearchPill) {
        setShowHeaderSearch(false);
        return;
      }

      observer = new IntersectionObserver(
        ([entry]) => {
          setShowHeaderSearch(!entry.isIntersecting);
        },
        { threshold: 0, rootMargin: "-80px 0px 0px 0px" }
      );

      observer.observe(heroSearchPill);
    }, 100);

    return () => {
      clearTimeout(timeout);
      if (observer) observer.disconnect();
    };
  }, [pathname]);
  
  


  return (
    <nav
      ref={navRef}
      aria-label="Main navigation"
      className={`hidden lg:inline-flex lg:h-[60px] lg:items-center flex-wrap
        fixed top-[14px] left-0 w-full px-[4%] pt-[7px] pb-[9px]
        z-[100] fill-white transition-[background-color,opacity] duration-300 ease-in-out
        border-b [&_a]:text-inherit
        ${isScrolled
          ? 'bg-white/90 backdrop-blur-[10px] text-black border-black/10 drop-shadow-[0px_4px_24px_rgba(255,90,95,0.1)]'
          : 'bg-transparent text-white border-transparent'
        }`}
    >
      <div className="cursor-pointer inline-flex items-center">
        <Link href="/" className="inline-flex items-center gap-2">
          <Image
            src={IMAGES.logo_icon}
            alt="Sukuto — Home"
            className="align-middle"
            width={36}
            height={36}
          />
          <span className="text-[20px] font-semibold tracking-[0.5px] text-primary">Sukuto</span>
        </Link>
      </div>
      {(PAGE_URLS_WITHOUT_SEARCH.includes(pathname) || showHeaderSearch) && (
        <div
          className={`flex-1 mx-[14px] hidden lg:block ${
            !PAGE_URLS_WITHOUT_SEARCH.includes(pathname) ? "animate-navSearchSlideIn" : ""
          }`}
        >
          <SearchPill type="header" />
        </div>
      )}
      <button
        className="lg:hidden ml-auto"
        onClick={() => setShowmenu((prev) => !prev)}
        aria-label="Menu"
        aria-expanded={showmenu}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
          className="w-8 h-8"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
          />
        </svg>
      </button>
      <div
        className={`lg:inline-flex w-full lg:w-auto mt-4 lg:mt-0 lg:ml-auto ${
          showmenu ? "" : "hidden"
        }`}
      >
        <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-8">
          <Link
            href="/monthly-bike-rental/"
            onClick={() => dispatch(setCityname({ id: "", name: "" }))}
            className={`pb-2 lg:pb-0 border-b-2 border-transparent transition-[border-color,opacity] duration-300
              text-[13px] cursor-pointer font-medium flex
              ${isScrolled ? 'hover:text-black hover:border-black' : 'hover:text-white hover:border-white'}
              ${pathname === "/monthly-bike-rental/" ? '!text-primary !border-primary' : ''}`}
          >
            Monthly Rentals
          </Link>
          <Link
            href="/blog/"
            onClick={() => dispatch(setCityname({ id: "", name: "" }))}
            className={`pb-2 lg:pb-0 border-b-2 border-transparent transition-[border-color,opacity] duration-300
              text-[13px] cursor-pointer font-medium flex
              ${isScrolled ? 'hover:text-black hover:border-black' : 'hover:text-white hover:border-white'}
              ${pathname === "/blog/" ? '!text-primary !border-primary' : ''}`}
          >
            Blogs
          </Link>
          <Link
            href="/become-partner/"
            onClick={() => dispatch(setCityname({ id: "", name: "" }))}
            className={`pb-2 lg:pb-0 border-b-2 border-transparent transition-[border-color,opacity] duration-300
              text-[13px] cursor-pointer font-medium flex
              ${isScrolled ? 'hover:text-black hover:border-black' : 'hover:text-white hover:border-white'}
              ${pathname === "/become-partner/" ? '!text-primary !border-primary' : ''}`}
          >
            Partner with Us
          </Link>
          <MenuItem label="Cart" path={`${BASE_URL_CITY}/cart/`} cartCount={isCartLoaded ? totalCartCount : null} />
        </div>
        <ProfileMenu refetchCart={refetchCart} />
      </div>
    </nav>
  );
};

export default NavBar;
